import { useState, useEffect } from 'react';
import { RandomCompany, QueryGroup } from '../types/randomCompanies';

const STORAGE_KEY = 'random-companies-discovery';
const GROUPS_STORAGE_KEY = 'random-companies-groups';

export const useRandomCompanies = () => {
  const [companies, setCompanies] = useState<RandomCompany[]>([]);
  const [queryGroups, setQueryGroups] = useState<QueryGroup[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const savedCompanies = localStorage.getItem(STORAGE_KEY);
      const savedGroups = localStorage.getItem(GROUPS_STORAGE_KEY);
      
      if (savedCompanies) {
        const parsedCompanies = JSON.parse(savedCompanies);
        if (Array.isArray(parsedCompanies) && parsedCompanies.length > 0) {
          setCompanies(parsedCompanies);
          console.log(`Restored ${parsedCompanies.length} companies from localStorage`);
        }
      }
      
      if (savedGroups) {
        const parsedGroups = JSON.parse(savedGroups);
        if (Array.isArray(parsedGroups) && parsedGroups.length > 0) {
          setQueryGroups(parsedGroups);
          console.log(`Restored ${parsedGroups.length} query groups from localStorage`);
        }
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  }, []);

  // Save to localStorage whenever companies or groups change
  useEffect(() => {
    if (companies.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(companies));
      } catch (error) {
        console.error('Error saving companies to localStorage:', error);
      }
    }
  }, [companies]);

  useEffect(() => {
    if (queryGroups.length > 0) {
      try {
        localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(queryGroups));
      } catch (error) {
        console.error('Error saving groups to localStorage:', error);
      }
    }
  }, [queryGroups]);

  // Add jiggle animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCompanies(prev => {
        const unvotedCompanies = prev.filter(c => !c.voted);
        if (unvotedCompanies.length === 0) return prev;

        // Reset all jiggling first
        const resetJiggling = prev.map(c => ({ ...c, jigglingButton: null }));

        // Randomly select 1-2 companies to jiggle
        const numToJiggle = Math.min(Math.floor(Math.random() * 2) + 1, unvotedCompanies.length);
        const companiesToJiggle = [...unvotedCompanies]
          .sort(() => 0.5 - Math.random())
          .slice(0, numToJiggle);

        return resetJiggling.map(company => {
          const shouldJiggle = companiesToJiggle.find(c => c.id === company.id);
          if (shouldJiggle && !company.voted) {
            return {
              ...company,
              jigglingButton: Math.random() > 0.5 ? 'thumbsUp' : 'thumbsDown'
            };
          }
          return company;
        });
      });
    }, 3000); // Jiggle every 3 seconds

    return () => clearInterval(interval);
  }, [companies.length]);

  // Clear newly voted highlight after 3 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCompanies(prev => 
        prev.map(company => ({
          ...company,
          isNewlyVoted: false
        }))
      );
    }, 3000);

    return () => clearTimeout(timeout);
  }, [companies.filter(c => c.isNewlyVoted).length]);

  const addCompanies = (newCompanies: RandomCompany[], groupId: string) => {
    setCompanies(prev => [...prev, ...newCompanies]);
  };

  const addQueryGroup = (group: QueryGroup) => {
    setQueryGroups(prev => [...prev, group]);
  };

  const removeGroup = (groupId: string) => {
    setCompanies(prev => prev.filter(company => company.groupId !== groupId));
    setQueryGroups(prev => prev.filter(g => g.id !== groupId));
  };

  const clearAll = () => {
    setCompanies([]);
    setQueryGroups([]);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(GROUPS_STORAGE_KEY);
  };

  const voteOnCompany = (companyId: string, vote: 'good' | 'bad') => {
    setCompanies(prev => 
      prev.map(company => 
        company.id === companyId 
          ? { ...company, voted: vote, jigglingButton: null, isNewlyVoted: true, isInEditMode: false }
          : company
      )
    );
  };

  const toggleExpand = (companyId: string) => {
    setCompanies(prev => 
      prev.map(company => 
        company.id === companyId 
          ? { ...company, hasOverlay: !company.hasOverlay }
          : { ...company, hasOverlay: false } // Close other overlays
      )
    );
  };

  const closeOverlay = () => {
    setCompanies(prev => 
      prev.map(company => ({ ...company, hasOverlay: false }))
    );
  };

  const editCompany = (companyId: string) => {
    setCompanies(prev => 
      prev.map(company => 
        company.id === companyId 
          ? { ...company, isInEditMode: true, hasOverlay: false }
          : company
      )
    );
  };

  return {
    companies,
    queryGroups,
    addCompanies,
    addQueryGroup,
    removeGroup,
    clearAll,
    voteOnCompany,
    toggleExpand,
    closeOverlay,
    editCompany
  };
}; 